import React, { useMemo } from 'react'
import { makeStyles } from '@mui/styles'
import { useSelector } from 'react-redux'

import ThemeInterface from '@/theme-interface'
import lang from '@/lang'
import { switchTenant } from '@/redux/ducks/user/info'
import { dispatch } from '@/redux/ducks/lists'
import { closeTenantSelection, showLoader, hideLoader } from '@/redux/ducks/tenant/selection'
import { toast } from '@/modules/base/components/toast/toastConfig'
import TenantSelectionItemBase from './tenant-selection-item-base'

interface restoreType {
  jobStatus: string
}

interface TenantSelectionSwitchItemProps {
  tenantId: string,
  tenantName: string,
  companyName: string,
  tenantRestores?: Array<restoreType>,
  hideSeperator?: boolean,
  isDemo?: boolean,
  iconColors?: { color: string, backgroundColor: string }
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  action: {
    fontSize: theme.fontSizes.smaller,
    color: theme.primaryBright,
    cursor: 'pointer',
  },
}), { name: 'TenantSelectionSwitchItem' })

const TenantSelectionSwitchItem = (props: TenantSelectionSwitchItemProps) => {
  const classes = useStyles()
  const {
    isDemo,
    tenantId,
    tenantName,
    companyName,
    tenantRestores,
    hideSeperator,
    iconColors,
  } = props
  const {
    tenantId: activeTenantId,
  } = useSelector(state => state.user.info.activeTenant)
  const subscriptions = useSelector(state => state.subscription.details.subscriptions)
  const isLoading = useSelector(state => state.tenant.selection.isLoading)
  const isTaxAccountant = useMemo(() => Boolean(subscriptions.find(sub => sub.isTaxAccountant)), [subscriptions])

  const getDisplayName = ({ tenantName, companyName }) => {
    return tenantName || companyName || (isTaxAccountant ? lang.tenant.newOffice : lang.tenant.newTenant)
  }

  const selectTenantDone = (error, tenant): void => {
    if (!error) {
      toast.showToast({ type: 'info', message: lang.tenant.tenantActivated(getDisplayName(tenant)) })
    } else {
      let errorMessage = lang.defaultErrorMessage
      if (error?.body?.meta?.switch) {
        errorMessage = lang.tenant.switchNotAllowed
      }

      toast.showToast({ type: 'error', message: errorMessage })
    }

    dispatch(hideLoader())
  }

  const changeTenant = (): void => {
    if (isLoading) { return }

    if (tenantId !== activeTenantId) {
      dispatch(showLoader())
      dispatch(closeTenantSelection())
      dispatch(switchTenant({ tenantId }, selectTenantDone))
    }
  }

  const restoreInAction = tenantRestores?.some((restore) => ['waiting', 'queued', 'active'].includes(restore.jobStatus))
  const action = <div className={classes.action}>{restoreInAction ? lang.tenant.restoreLabel : lang.tenant.switchLabel }</div>
  return (
    <TenantSelectionItemBase
      isDemo={isDemo}
      hideSeperator={hideSeperator}
      tenantId={tenantId}
      companyName={companyName}
      tenantName={tenantName}
      action={action}
      iconColors={{ color: iconColors?.color, backgroundColor: iconColors?.backgroundColor }}
      onClick={changeTenant} />
  )
}

export default TenantSelectionSwitchItem
